import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const DEFAULT_DURATION_MS = 7000
const VIEWPORT_GUTTER = 16
const ANCHOR_GAP = 8
const TOAST_WIDTH = 420

/**
 * Control-affixed Ask lock hint.
 *
 * Uses the design-system `.app-toast` / `.app-toast--info` visual, but is
 * positioned under the triggering control instead of the global toast slot
 * (bottom-right). That slot is for async success/error, not a locked-field
 * explanation.
 */
export default function AskLockedHint({
  open = false,
  onClose,
  anchorRef,
  duration = DEFAULT_DURATION_MS,
}) {
  const panelRef = useRef(null)
  const [position, setPosition] = useState({ top: 72, left: VIEWPORT_GUTTER, width: TOAST_WIDTH })

  useLayoutEffect(() => {
    if (!open) return undefined

    function resolveAnchor() {
      const preferred = anchorRef?.current
      if (preferred && preferred.getBoundingClientRect().height > 0) return preferred
      const fab = document.querySelector('[data-testid="ask-the-cook-fab"]')
      if (fab && fab.getBoundingClientRect().height > 0) return fab
      return document.querySelector('.top-nav')
    }

    function updatePosition() {
      const anchor = resolveAnchor()
      const width = Math.min(TOAST_WIDTH, window.innerWidth - VIEWPORT_GUTTER * 2)
      if (!anchor) {
        setPosition({ top: 72, left: VIEWPORT_GUTTER, width })
        return
      }

      const rect = anchor.getBoundingClientRect()
      const panelHeight = panelRef.current?.offsetHeight || 120
      const spaceBelow = window.innerHeight - rect.bottom
      const placeBelow = spaceBelow >= panelHeight + ANCHOR_GAP || rect.top < window.innerHeight / 2
      const top = placeBelow
        ? rect.bottom + ANCHOR_GAP
        : Math.max(VIEWPORT_GUTTER, rect.top - panelHeight - ANCHOR_GAP)
      const preferredLeft = rect.left > 0 ? rect.left : VIEWPORT_GUTTER
      const left = Math.max(
        VIEWPORT_GUTTER,
        Math.min(preferredLeft, window.innerWidth - width - VIEWPORT_GUTTER)
      )
      setPosition({ top, left, width })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open, anchorRef])

  useEffect(() => {
    if (!open) return undefined
    const tid = window.setTimeout(() => onClose?.(), duration)
    return () => window.clearTimeout(tid)
  }, [open, duration, onClose])

  useEffect(() => {
    if (!open) return undefined

    function handlePointerDown(event) {
      const target = event.target
      if (panelRef.current?.contains(target)) return
      if (anchorRef?.current?.contains(target)) return
      onClose?.()
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose, anchorRef])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="app-toast-anchor app-toast-anchor--anchored"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
      }}
    >
      <div
        ref={panelRef}
        className="app-toast app-toast--info"
        role="status"
        aria-live="polite"
        data-testid="ask-locked-hint"
      >
        <span className="app-toast__icon" aria-hidden="true">✦</span>
        <div className="app-toast__body">
          <div className="app-toast__title">Create an account to get help from the Captain</div>
          <div className="app-toast__text">
            The Captain can answer questions about recipes, pantry, and shopping once your kitchen is saved.
          </div>
        </div>
        <button
          type="button"
          className="app-toast__dismiss"
          aria-label="Dismiss notification"
          onClick={() => onClose?.()}
        >
          ×
        </button>
      </div>
    </div>,
    document.body
  )
}
